/**
 * Orchestra AI Platform - A2A Capability Discovery Service
 * 
 * Discovers capabilities from all 4 A2A servers and transforms them into API format
 * 
 * @fileoverview Service for discovering and transforming A2A capabilities
 */

import { A2AClient } from '../a2a/client';
import { config } from '../config/environment';
import { callAIProxy } from '../lib/edgeFunctions';

/**
 * Server configuration
 */
const SERVER_CONFIG = {
  chinese: {
    url: config.aiServers.chinese,
    agentId: 'orchestra-chinese-client',
    label: 'Chinese AI',
  },
  english: {
    url: config.aiServers.english,
    agentId: 'orchestra-english-client',
    label: 'English AI',
  },
  maths: {
    url: config.aiServers.maths,
    agentId: 'orchestra-maths-client',
    label: 'Math AI',
  },
  myclass: {
    url: config.aiServers.myclass,
    agentId: 'orchestra-myclass-client',
    label: 'MyClass AI',
  },
};

/**
 * Map capability names to categories
 * @param {string} capabilityName - Capability name
 * @param {string} server - Server type
 * @returns {string} Category name
 */
function getCategoryForCapability(capabilityName, server) {
  // Knowledge Graph
  if (capabilityName.includes('knowledge_graph') || capabilityName.includes('kg_')) {
    return 'knowledge_graph';
  }
  
  // Grammar
  if (capabilityName.includes('grammar') || capabilityName.includes('ess_')) {
    return 'grammar';
  }
  
  // Vocabulary
  if (capabilityName.includes('vocab') || capabilityName.includes('phrase')) {
    return 'vocabulary';
  }
  
  // Writing
  if (capabilityName.includes('composition') || capabilityName.includes('writing') || capabilityName.includes('essay')) {
    return 'writing';
  }
  
  // HKDSE
  if (capabilityName.includes('hkdse')) {
    return 'hkdse';
  }
  
  // Primary
  if (capabilityName.includes('primary') || capabilityName.includes('_p')) {
    return 'primary';
  }
  
  // DeepSeek AI
  if (capabilityName.includes('deepseek') || capabilityName.includes('generate_') || capabilityName.includes('expand_')) {
    return 'ai_services';
  }
  
  // Student Management (MyClass)
  if (capabilityName.includes('student') || capabilityName.includes('teacher') || capabilityName.includes('class')) {
    return 'student_management';
  }
  
  // Homework (MyClass)
  if (capabilityName.includes('homework')) {
    return 'homework';
  }
  
  // Library (MyClass)
  if (capabilityName.includes('library') || capabilityName.includes('book')) {
    return 'library';
  }
  
  // Calendar (MyClass)
  if (capabilityName.includes('calendar') || capabilityName.includes('event')) {
    return 'calendar';
  }
  
  // Default category
  return 'other';
}

/**
 * Transform A2A capability to API format
 * @param {Object} capability - A2A capability object
 * @param {string} server - Server type (chinese, english, maths, myclass)
 * @param {number} index - Index for unique ID
 * @returns {Object} API object
 */
function transformCapabilityToAPI(capability, server, index) {
  const category = getCategoryForCapability(capability.name, server);
  
  // Format name: convert snake_case to Title Case
  const formattedName = capability.name
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase());
  
  return {
    id: `${server}_${capability.name}_${index}`,
    name: formattedName,
    description: capability.description || `Execute ${formattedName}`,
    server: server,
    endpoint: `/a2a/execute`,
    method: 'POST',
    category: category,
    parameters: Array.isArray(capability.parameters) ? capability.parameters : [],
    returns: capability.returns || 'result',
    timeout: capability.timeout || 30,
    is_favorite: false,
    capability_name: capability.name, // Store original capability name for execution
  };
}

/**
 * Parse OpenAPI spec and extract capabilities
 * @param {Object} openApiSpec - OpenAPI specification object
 * @param {string} server - Server type
 * @returns {Object[]} Array of API objects
 */
function parseOpenAPISpec(openApiSpec, server) {
  const apis = [];
  const paths = openApiSpec.paths || {};
  const components = openApiSpec.components || {};
  const schemas = components.schemas || {};

  for (const [path, pathItem] of Object.entries(paths)) {
    for (const [method, operation] of Object.entries(pathItem)) {
      if (['get', 'post', 'put', 'delete', 'patch'].includes(method.toLowerCase())) {
        const operationObj = operation;
        if (!operationObj) continue;

        const apiId = `${server}_${method}_${path.replace(/\//g, '_').replace(/[{}]/g, '')}`;
        const formattedName = (operationObj.summary || operationObj.operationId || `${method.toUpperCase()} ${path}`)
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (l) => l.toUpperCase());

        // Extract parameters
        const parameters = (operationObj.parameters || []).map(param => ({
          name: param.name,
          in: param.in,
          required: param.required || false,
          schema: param.schema || {},
          description: param.description || '',
        }));

        // Extract request body schema
        const requestBody = operationObj.requestBody;
        if (requestBody && requestBody.content) {
          const content = Object.values(requestBody.content)[0];
          if (content && content.schema) {
            const schema = content.schema;
            if (schema.properties) {
              for (const [propName, propSchema] of Object.entries(schema.properties)) {
                parameters.push({
                  name: propName,
                  in: 'body',
                  required: schema.required && schema.required.includes(propName),
                  schema: propSchema,
                  description: propSchema.description || '',
                });
              }
            }
          }
        }

        const api = {
          id: apiId,
          name: formattedName,
          description: operationObj.description || operationObj.summary || `Execute ${formattedName}`,
          server: server,
          endpoint: path,
          method: method.toUpperCase(),
          category: getCategoryForCapability(operationObj.operationId || path, server),
          parameters: parameters,
          returns: operationObj.responses?.['200']?.description || 'result',
          timeout: 30,
          is_favorite: false,
          openapi_operation_id: operationObj.operationId,
        };

        apis.push(api);
      }
    }
  }

  return apis;
}

/**
 * Try to fetch OpenAPI spec from common endpoints
 * @param {string} baseUrl - Base URL of the server
 * @returns {Promise<Object|null>} OpenAPI spec or null
 */
async function fetchOpenAPISpec(baseUrl) {
  const openApiEndpoints = [
    '/api/docs',
    '/docs/openapi.json',
    '/docs/openapi.yaml',
    '/openapi.json',
    '/openapi.yaml',
    '/swagger.json',
    '/swagger.yaml',
    '/api/openapi.json',
  ];

  for (const endpoint of openApiEndpoints) {
    try {
      const response = await fetch(`${baseUrl}${endpoint}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json, application/yaml, */*',
        },
      });

      if (response.ok) {
        const contentType = response.headers.get('content-type') || '';
        let data;

        if (contentType.includes('yaml') || endpoint.includes('.yaml')) {
          // For YAML, we'd need a YAML parser, but for now, try JSON
          console.warn(`[A2A Discovery] YAML format detected at ${endpoint}, skipping (YAML parser not available)`);
          continue;
        } else {
          data = await response.json();
        }

        // Check if it's a valid OpenAPI spec
        if (data.openapi || data.swagger) {
          console.log(`[A2A Discovery] Found OpenAPI spec at ${endpoint}`);
          return data;
        }
      }
    } catch (error) {
      // Continue to next endpoint
      continue;
    }
  }

  return null;
}

/**
 * Discover capabilities from a single server
 * @param {string} server - Server type
 * @returns {Promise<Object[]>} Array of API objects
 */
async function discoverServerCapabilities(server) {
  const serverConfig = SERVER_CONFIG[server];
  if (!serverConfig) {
    console.warn(`[A2A Discovery] Unknown server: ${server}`);
    return [];
  }

  try {
    console.log(`[A2A Discovery] Discovering capabilities from ${serverConfig.label} (${serverConfig.url})...`);
    
    // Try A2A discovery first
    let capabilities = null;
    let discoveryMethod = 'unknown';
    try {
      const client = new A2AClient(serverConfig.url, serverConfig.agentId, {
        timeout: 30000,
        enableLogging: config.isDevelopment,
      });

      capabilities = await client.discoverCapabilities(true); // Force refresh
      
      // Validate capabilities
      if (capabilities && Array.isArray(capabilities) && capabilities.length > 0) {
        discoveryMethod = 'a2a';
        console.log(`[A2A Discovery] ✓ Found ${capabilities.length} capabilities via A2A from ${serverConfig.label}`);
      } else {
        console.warn(`[A2A Discovery] A2A discovery returned empty or invalid result for ${serverConfig.label}`);
        capabilities = null;
      }
    } catch (a2aError) {
      const isCorsError = a2aError.message && (
        a2aError.message.includes('CORS') ||
        a2aError.message.includes('blocked by CORS policy') ||
        a2aError.code === 'ERR_NETWORK'
      );
      
      if (isCorsError) {
        console.warn(`[A2A Discovery] CORS error for ${serverConfig.label}, trying Edge Function proxy...`);
        
        // Try using Edge Function proxy to bypass CORS
        try {
          const proxyResponse = await callAIProxy({
            server: server,
            endpoint: '/a2a/discover',
            method: 'POST',
            data: {
              protocol: 'a2a/1.0',
              messageType: 'request',
              messageId: `proxy-${Date.now()}`,
              timestamp: new Date().toISOString(),
              sender: {
                agentId: serverConfig.agentId,
                agentType: 'frontend',
              },
              receiver: {
                agentId: serverConfig.url.includes('chinese') ? 'a2achinese-server' :
                         serverConfig.url.includes('english') ? 'a2aenglish-server' :
                         serverConfig.url.includes('maths') ? 'a2amaths-server' :
                         'a2amyclass-server',
                agentType: 'backend',
              },
              payload: {
                task: 'capability-discovery',
                parameters: {},
              },
            },
          });
          
          // Extract capabilities from proxy response
          // Handle different response formats
          let proxyCapabilities = null;
          if (proxyResponse.data && proxyResponse.data.payload && proxyResponse.data.payload.result && proxyResponse.data.payload.result.capabilities) {
            proxyCapabilities = proxyResponse.data.payload.result.capabilities;
          } else if (proxyResponse.data && proxyResponse.data.capabilities) {
            proxyCapabilities = proxyResponse.data.capabilities;
          } else if (proxyResponse.capabilities) {
            proxyCapabilities = proxyResponse.capabilities;
          } else if (Array.isArray(proxyResponse.data)) {
            proxyCapabilities = proxyResponse.data;
          }
          
          if (proxyCapabilities && Array.isArray(proxyCapabilities) && proxyCapabilities.length > 0) {
            capabilities = proxyCapabilities;
            discoveryMethod = 'proxy';
            console.log(`[A2A Discovery] ✓ Found ${capabilities.length} capabilities via Edge Function proxy from ${serverConfig.label}`);
          } else {
            console.warn(`[A2A Discovery] Edge Function proxy returned empty result for ${serverConfig.label}`);
          }
        } catch (proxyError) {
          console.warn(`[A2A Discovery] Edge Function proxy also failed for ${serverConfig.label}, trying OpenAPI format...`, proxyError.message);
          
          // Try OpenAPI format as final fallback
          try {
            const openApiSpec = await fetchOpenAPISpec(serverConfig.url);
            if (openApiSpec) {
              const openApiApis = parseOpenAPISpec(openApiSpec, server);
              if (openApiApis.length > 0) {
                discoveryMethod = 'openapi';
                console.log(`[A2A Discovery] ✓ Found ${openApiApis.length} capabilities via OpenAPI from ${serverConfig.label}`);
                // OpenAPI APIs are already in the correct format, return them directly
                // Don't transform them again - they're already API objects
                return openApiApis;
              } else {
                console.warn(`[A2A Discovery] OpenAPI parsing returned no APIs for ${serverConfig.label}`);
              }
            }
          } catch (openApiError) {
            console.error(`[A2A Discovery] All discovery methods failed for ${serverConfig.label}:`, openApiError.message);
          }
        }
      } else {
        console.warn(`[A2A Discovery] A2A discovery failed for ${serverConfig.label}, trying OpenAPI format...`, a2aError.message);
        
        // Try OpenAPI format as fallback
        try {
          const openApiSpec = await fetchOpenAPISpec(serverConfig.url);
          if (openApiSpec) {
            const openApiApis = parseOpenAPISpec(openApiSpec, server);
            if (openApiApis.length > 0) {
              discoveryMethod = 'openapi';
              console.log(`[A2A Discovery] ✓ Found ${openApiApis.length} capabilities via OpenAPI from ${serverConfig.label}`);
              // OpenAPI APIs are already in the correct format, return them directly
              // Don't transform them again - they're already API objects
              return openApiApis;
            } else {
              console.warn(`[A2A Discovery] OpenAPI parsing returned no APIs for ${serverConfig.label}`);
            }
          }
        } catch (openApiError) {
          console.error(`[A2A Discovery] OpenAPI discovery also failed for ${serverConfig.label}:`, openApiError.message);
        }
      }
    }
    
    if (!capabilities || !Array.isArray(capabilities) || capabilities.length === 0) {
      console.warn(`[A2A Discovery] No capabilities found for ${serverConfig.label} (method: ${discoveryMethod})`);
      return [];
    }

    console.log(`[A2A Discovery] Processing ${capabilities.length} capabilities from ${serverConfig.label} (method: ${discoveryMethod})...`);
    
    // Log sample capability structure
    if (capabilities.length > 0) {
      console.log(`[A2A Discovery] Sample capability structure:`, {
        hasName: !!capabilities[0].name,
        hasDescription: !!capabilities[0].description,
        name: capabilities[0].name,
        keys: Object.keys(capabilities[0]),
      });
    }

    // Transform capabilities to API format
    let transformedCount = 0;
    let errorCount = 0;
    const apis = capabilities.map((cap, index) => {
      if (!cap) {
        errorCount++;
        console.warn(`[A2A Discovery] Null capability at index ${index}`);
        return null;
      }
      
      if (!cap.name) {
        errorCount++;
        console.warn(`[A2A Discovery] Capability missing name at index ${index}:`, cap);
        return null;
      }
      
      try {
        const api = transformCapabilityToAPI(cap, server, index);
        transformedCount++;
        return api;
      } catch (error) {
        errorCount++;
        console.error(`[A2A Discovery] Error transforming capability at index ${index}:`, error.message, {
          capabilityName: cap.name,
          capability: cap,
        });
        return null;
      }
    }).filter(api => api !== null); // Remove null entries

    console.log(`[A2A Discovery] Transformation summary for ${serverConfig.label}:`);
    console.log(`  - Total capabilities: ${capabilities.length}`);
    console.log(`  - Successfully transformed: ${transformedCount}`);
    console.log(`  - Errors: ${errorCount}`);
    console.log(`  - Final API count: ${apis.length}`);
    
    if (apis.length === 0 && capabilities.length > 0) {
      console.error(`[A2A Discovery] ⚠️ WARNING: All ${capabilities.length} capabilities failed transformation!`);
      console.error(`[A2A Discovery] First capability that failed:`, capabilities[0]);
    }
    
    return apis;
  } catch (error) {
    const errorMessage = error.message || error.toString();
    console.error(`[A2A Discovery] ✗ Failed to discover capabilities from ${serverConfig.label}:`, errorMessage);
    
    // Don't throw - return empty array so other servers can still be discovered
    return [];
  }
}

/**
 * Discover capabilities from all A2A servers
 * @returns {Promise<Object[]>} Array of all API objects
 */
export async function discoverAllCapabilities() {
  console.log('[A2A Discovery] Starting capability discovery from all servers...');
  
  const servers = ['chinese', 'english', 'maths', 'myclass'];
  
  // Discover from all servers in parallel
  const results = await Promise.allSettled(
    servers.map(server => discoverServerCapabilities(server))
  );

  // Combine all successful results
  const allAPIs = [];
  results.forEach((result, index) => {
    const serverName = servers[index];
    if (result.status === 'fulfilled') {
      const serverAPIs = result.value || [];
      console.log(`[A2A Discovery] ✓ ${serverName}: ${serverAPIs.length} APIs`);
      if (serverAPIs.length > 0) {
        console.log(`[A2A Discovery] Sample API from ${serverName}:`, serverAPIs[0]);
      }
      allAPIs.push(...serverAPIs);
    } else {
      console.error(`[A2A Discovery] ✗ Failed to discover from ${serverName}:`, result.reason);
    }
  });

  console.log(`[A2A Discovery] ========================================`);
  console.log(`[A2A Discovery] Total APIs discovered: ${allAPIs.length}`);
  console.log(`[A2A Discovery] APIs by server:`, {
    chinese: allAPIs.filter(api => api.server === 'chinese').length,
    english: allAPIs.filter(api => api.server === 'english').length,
    maths: allAPIs.filter(api => api.server === 'maths').length,
    myclass: allAPIs.filter(api => api.server === 'myclass').length,
  });
  console.log(`[A2A Discovery] ========================================`);
  return allAPIs;
}

/**
 * Discover capabilities from a specific server
 * @param {string} server - Server type (chinese, english, maths, myclass)
 * @returns {Promise<Object[]>} Array of API objects
 */
export async function discoverServerAPIs(server) {
  return await discoverServerCapabilities(server);
}

